export const WORKOS_OAUTH_LOGIN_PATH = '/oauth/workos/login';

const PENDING_STORAGE_KEY = 'vectormethods.oauth.workos.pending.v1';
const ACTIVE_STORAGE_KEY = 'vectormethods.oauth.workos.active.v1';
const FAILURE_STORAGE_KEY = 'vectormethods.oauth.workos.failure.v1';
const MAX_EXTERNAL_AUTH_ID_LENGTH = 255;
const STORAGE_VERSION = 1;

export type WorkOSOAuthFailureReason =
  | 'account_changed'
  | 'completion_failed'
  | 'interrupted'
  | 'invalid'
  | 'missing'
  | 'storage_unavailable';

let volatileCaptureFailure: WorkOSOAuthFailureReason | null = null;

interface PendingWorkOSOAuth {
  version: 1;
  externalAuthId: string;
  capturedAt: number;
  boundUserId: string | null;
}

interface ActiveWorkOSOAuth {
  version: 1;
  attemptId: string;
  userId: string;
  startedAt: number;
}

interface FailedWorkOSOAuth {
  version: 1;
  reason: WorkOSOAuthFailureReason;
  failedAt: number;
}

export interface WorkOSOAuthCaptureResult {
  captured: boolean;
  hadExternalAuthId: boolean;
  sanitizedSearch: string;
  failureReason?: WorkOSOAuthFailureReason;
}

export type WorkOSOAuthBindingResult =
  | { kind: 'bound' | 'already_bound' | 'no_pending' }
  | { kind: 'invalidated'; reason: WorkOSOAuthFailureReason };

export type WorkOSOAuthClaimResult =
  | {
      kind: 'claimed';
      attemptId: string;
      externalAuthId: string;
      userId: string;
    }
  | {
      kind: 'in_progress';
      attemptId: string;
      userId: string;
    }
  | {
      kind: 'unavailable';
      reason: WorkOSOAuthFailureReason;
    };

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const isFiniteTimestamp = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value) && value >= 0
);

const isValidExternalAuthId = (value: string): boolean => (
  value.length > 0
  && value.length <= MAX_EXTERNAL_AUTH_ID_LENGTH
  && /^[\x21-\x7e]+$/.test(value)
);

const parseStoredJson = (storage: Storage, key: string): unknown => {
  const raw = storage.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    storage.removeItem(key);
    return null;
  }
};

const parsePending = (value: unknown): PendingWorkOSOAuth | null => {
  if (!isRecord(value) || value.version !== STORAGE_VERSION) return null;
  const externalAuthId = typeof value.externalAuthId === 'string'
    ? value.externalAuthId
    : '';
  const boundUserId = value.boundUserId === null || typeof value.boundUserId === 'string'
    ? value.boundUserId
    : undefined;
  if (
    !isValidExternalAuthId(externalAuthId)
    || !isFiniteTimestamp(value.capturedAt)
    || boundUserId === undefined
    || (typeof boundUserId === 'string' && boundUserId.trim().length === 0)
  ) {
    return null;
  }
  return {
    version: STORAGE_VERSION,
    externalAuthId,
    capturedAt: value.capturedAt,
    boundUserId,
  };
};

const parseActive = (value: unknown): ActiveWorkOSOAuth | null => {
  if (!isRecord(value) || value.version !== STORAGE_VERSION) return null;
  if (
    typeof value.attemptId !== 'string'
    || !value.attemptId
    || typeof value.userId !== 'string'
    || !value.userId
    || !isFiniteTimestamp(value.startedAt)
  ) {
    return null;
  }
  return {
    version: STORAGE_VERSION,
    attemptId: value.attemptId,
    userId: value.userId,
    startedAt: value.startedAt,
  };
};

const parseFailure = (value: unknown): FailedWorkOSOAuth | null => {
  if (!isRecord(value) || value.version !== STORAGE_VERSION) return null;
  const knownReasons = new Set<WorkOSOAuthFailureReason>([
    'account_changed',
    'completion_failed',
    'interrupted',
    'invalid',
    'missing',
    'storage_unavailable',
  ]);
  if (
    typeof value.reason !== 'string'
    || !knownReasons.has(value.reason as WorkOSOAuthFailureReason)
    || !isFiniteTimestamp(value.failedAt)
  ) {
    return null;
  }
  return {
    version: STORAGE_VERSION,
    reason: value.reason as WorkOSOAuthFailureReason,
    failedAt: value.failedAt,
  };
};

const writeFailure = (
  storage: Storage,
  reason: WorkOSOAuthFailureReason,
  now: number,
): void => {
  storage.setItem(FAILURE_STORAGE_KEY, JSON.stringify({
    version: STORAGE_VERSION,
    reason,
    failedAt: now,
  } satisfies FailedWorkOSOAuth));
};

const clearTransaction = (storage: Storage): void => {
  storage.removeItem(PENDING_STORAGE_KEY);
  storage.removeItem(ACTIVE_STORAGE_KEY);
};

const invalidateTransaction = (
  storage: Storage,
  reason: WorkOSOAuthFailureReason,
  now: number,
): void => {
  clearTransaction(storage);
  writeFailure(storage, reason, now);
};

const readPending = (
  storage: Storage,
  now: number,
): { pending: PendingWorkOSOAuth | null; reason?: WorkOSOAuthFailureReason } => {
  const rawPending = parseStoredJson(storage, PENDING_STORAGE_KEY);
  if (rawPending === null) return { pending: null };
  const pending = parsePending(rawPending);
  if (!pending) {
    invalidateTransaction(storage, 'invalid', now);
    return { pending: null, reason: 'invalid' };
  }
  return { pending };
};

const readActive = (
  storage: Storage,
  now: number,
): { active: ActiveWorkOSOAuth | null; reason?: WorkOSOAuthFailureReason } => {
  const rawActive = parseStoredJson(storage, ACTIVE_STORAGE_KEY);
  if (rawActive === null) return { active: null };
  const active = parseActive(rawActive);
  if (!active) {
    invalidateTransaction(storage, 'interrupted', now);
    return { active: null, reason: 'interrupted' };
  }
  return { active };
};

const readRecentFailure = (
  storage: Storage,
  _now: number,
): WorkOSOAuthFailureReason | null => {
  const rawFailure = parseStoredJson(storage, FAILURE_STORAGE_KEY);
  if (rawFailure === null) return null;
  const failure = parseFailure(rawFailure);
  if (!failure) {
    storage.removeItem(FAILURE_STORAGE_KEY);
    return null;
  }
  return failure.reason;
};

const createAttemptId = (): string => {
  const cryptoApi = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  const randomUUID = (cryptoApi as (Crypto & { randomUUID?: () => string }) | undefined)?.randomUUID;
  if (typeof randomUUID === 'function') {
    return randomUUID.call(cryptoApi);
  }
  if (cryptoApi?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    return Array.from(bytes, (byte) => (`0${byte.toString(16)}`).slice(-2)).join('');
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

export const isWorkOSOAuthLoginPath = (pathname: string): boolean => (
  (pathname.replace(/\/+$/, '') || '/') === WORKOS_OAUTH_LOGIN_PATH
);

export const getWorkOSOAuthSessionStorage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const captureWorkOSOAuthFromSearch = (
  search: string,
  storage: Storage | null,
  now: number = Date.now(),
): WorkOSOAuthCaptureResult => {
  const params = new URLSearchParams(search);
  const values = params.getAll('external_auth_id');
  if (values.length === 0) {
    return {
      captured: false,
      hadExternalAuthId: false,
      sanitizedSearch: search,
    };
  }

  params.delete('external_auth_id');
  const serialized = params.toString();
  const sanitizedSearch = serialized ? `?${serialized}` : '';
  if (!storage) {
    volatileCaptureFailure = 'storage_unavailable';
    return {
      captured: false,
      hadExternalAuthId: true,
      sanitizedSearch,
      failureReason: 'storage_unavailable',
    };
  }

  try {
    const externalAuthId = values.length === 1 ? values[0] : '';
    if (!isValidExternalAuthId(externalAuthId)) {
      invalidateTransaction(storage, 'invalid', now);
      volatileCaptureFailure = null;
      return {
        captured: false,
        hadExternalAuthId: true,
        sanitizedSearch,
        failureReason: 'invalid',
      };
    }

    const existingPending = parsePending(parseStoredJson(storage, PENDING_STORAGE_KEY));
    if (existingPending?.externalAuthId === externalAuthId) {
      // React StrictMode and repeated router effects may observe the same URL
      // more than once. Preserve any account binding instead of reopening it.
      volatileCaptureFailure = null;
      return {
        captured: true,
        hadExternalAuthId: true,
        sanitizedSearch,
      };
    }

    storage.setItem(PENDING_STORAGE_KEY, JSON.stringify({
      version: STORAGE_VERSION,
      externalAuthId,
      capturedAt: now,
      boundUserId: null,
    } satisfies PendingWorkOSOAuth));
    storage.removeItem(ACTIVE_STORAGE_KEY);
    storage.removeItem(FAILURE_STORAGE_KEY);
    volatileCaptureFailure = null;
    return {
      captured: true,
      hadExternalAuthId: true,
      sanitizedSearch,
    };
  } catch {
    try {
      clearTransaction(storage);
      storage.removeItem(FAILURE_STORAGE_KEY);
    } catch {
      // The volatile failure below still prevents this page instance from
      // submitting any older transaction left in inaccessible storage.
    }
    volatileCaptureFailure = 'storage_unavailable';
    return {
      captured: false,
      hadExternalAuthId: true,
      sanitizedSearch,
      failureReason: 'storage_unavailable',
    };
  }
};

export const bindPendingWorkOSOAuthToUser = (
  userId: string,
  storage: Storage | null,
  now: number = Date.now(),
): WorkOSOAuthBindingResult => {
  const normalizedUserId = userId.trim();
  if (volatileCaptureFailure) {
    return { kind: 'invalidated', reason: volatileCaptureFailure };
  }
  if (!storage || !normalizedUserId) {
    return { kind: 'invalidated', reason: 'storage_unavailable' };
  }
  try {
    const { active, reason: activeReason } = readActive(storage, now);
    if (active) {
      if (active.userId !== normalizedUserId) {
        invalidateTransaction(storage, 'account_changed', now);
        return { kind: 'invalidated', reason: 'account_changed' };
      }
      return { kind: 'already_bound' };
    }
    if (activeReason) {
      return { kind: 'invalidated', reason: activeReason };
    }

    const { pending, reason } = readPending(storage, now);
    if (!pending) {
      return reason
        ? { kind: 'invalidated', reason }
        : { kind: 'no_pending' };
    }
    if (pending.boundUserId && pending.boundUserId !== normalizedUserId) {
      invalidateTransaction(storage, 'account_changed', now);
      return { kind: 'invalidated', reason: 'account_changed' };
    }
    if (pending.boundUserId === normalizedUserId) {
      return { kind: 'already_bound' };
    }
    storage.setItem(PENDING_STORAGE_KEY, JSON.stringify({
      ...pending,
      boundUserId: normalizedUserId,
    } satisfies PendingWorkOSOAuth));
    return { kind: 'bound' };
  } catch {
    volatileCaptureFailure = 'storage_unavailable';
    return { kind: 'invalidated', reason: 'storage_unavailable' };
  }
};

export const claimPendingWorkOSOAuth = (
  userId: string,
  storage: Storage | null,
  now: number = Date.now(),
): WorkOSOAuthClaimResult => {
  const normalizedUserId = userId.trim();
  if (volatileCaptureFailure) {
    return { kind: 'unavailable', reason: volatileCaptureFailure };
  }
  if (!storage || !normalizedUserId) {
    return { kind: 'unavailable', reason: 'storage_unavailable' };
  }

  try {
    const { active, reason: activeReason } = readActive(storage, now);
    if (active) {
      if (active.userId !== normalizedUserId) {
        invalidateTransaction(storage, 'account_changed', now);
        return { kind: 'unavailable', reason: 'account_changed' };
      }
      return {
        kind: 'in_progress',
        attemptId: active.attemptId,
        userId: active.userId,
      };
    }
    if (activeReason) {
      return { kind: 'unavailable', reason: activeReason };
    }

    const { pending, reason: pendingReason } = readPending(storage, now);
    if (!pending) {
      return {
        kind: 'unavailable',
        reason: pendingReason || readRecentFailure(storage, now) || 'missing',
      };
    }
    if (pending.boundUserId && pending.boundUserId !== normalizedUserId) {
      invalidateTransaction(storage, 'account_changed', now);
      return { kind: 'unavailable', reason: 'account_changed' };
    }

    const attemptId = createAttemptId();
    storage.setItem(ACTIVE_STORAGE_KEY, JSON.stringify({
      version: STORAGE_VERSION,
      attemptId,
      userId: normalizedUserId,
      startedAt: now,
    } satisfies ActiveWorkOSOAuth));
    storage.removeItem(PENDING_STORAGE_KEY);
    storage.removeItem(FAILURE_STORAGE_KEY);
    return {
      kind: 'claimed',
      attemptId,
      externalAuthId: pending.externalAuthId,
      userId: normalizedUserId,
    };
  } catch {
    volatileCaptureFailure = 'storage_unavailable';
    return { kind: 'unavailable', reason: 'storage_unavailable' };
  }
};

export const finishWorkOSOAuthAttempt = (
  attemptId: string,
  storage: Storage | null,
  failureReason?: WorkOSOAuthFailureReason,
  now: number = Date.now(),
): void => {
  if (!storage) return;
  try {
    const active = parseActive(parseStoredJson(storage, ACTIVE_STORAGE_KEY));
    if (active?.attemptId === attemptId) {
      storage.removeItem(ACTIVE_STORAGE_KEY);
    }
    if (failureReason) {
      writeFailure(storage, failureReason, now);
    } else {
      storage.removeItem(FAILURE_STORAGE_KEY);
    }
  } catch {
    // The request is already one-shot. A storage cleanup failure must not cause
    // a retry or expose the handoff identifier again.
  }
};
